import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

type ExternalLinkContextType = {
  openLink: (url: string) => void;
};

const ExternalLinkContext = createContext<ExternalLinkContextType | undefined>(undefined);

export const ExternalLinkProvider = ({ children }: { children: ReactNode }) => {
  const [url, setUrl] = useState<string | null>(null);

  const openLink = useCallback((u: string) => setUrl(u), []);
  const close = useCallback(() => setUrl(null), []);

  return (
    <ExternalLinkContext.Provider value={{ openLink }}>
      {children}
      <AnimatePresence>
        {url && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-center justify-center"
            onClick={close}
            onKeyDown={(e) => e.key === "Escape" && close()}
          >
            {/* Overlay */}
            <div className="absolute inset-0 bg-kuro/80 backdrop-blur-sm" />

            {/* Close button — outside the iframe container */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2, delay: 0.1 }}
              onClick={(e) => { e.stopPropagation(); close(); }}
              className="absolute top-4 right-4 md:top-6 md:right-6 z-30 bg-shiro hover:bg-shiro/90 text-kuro rounded-full p-2.5 transition-colors shadow-lg"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </motion.button>

            {/* Modal — iframe container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative z-10 w-full h-[calc(100%-60px)] mt-[50px] md:w-[90vw] md:h-[85vh] md:max-w-5xl md:mt-0 md:rounded-lg overflow-hidden bg-background shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src={url}
                className="w-full h-full border-0"
                title="External content"
                allow="payment; clipboard-write"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ExternalLinkContext.Provider>
  );
};

export const useExternalLink = () => {
  const ctx = useContext(ExternalLinkContext);
  if (!ctx) throw new Error("useExternalLink must be used within ExternalLinkProvider");
  return ctx;
};
